import React, { useState, useEffect, useContext } from 'react';
import RequestService from '../../services/RequestService';
import TableComponent from '../../widgets/TableComponent';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import {
    Grid2 as Grid,
    Typography,
    Breadcrumbs,
    Link,
    Stack,
    Chip,
    TextField,
    Button,
    Box,
    Snackbar,
    Alert,
    useMediaQuery,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from '@mui/material';
import { UserContext } from '../../contexts/UserContext';
import { useTheme } from '@mui/material/styles';
import ProductDialog from '../../widgets/stores/ProductDialog';
import NewProductDialog from '../../widgets/stores/NewProductDialog';
import ProductList from '../../widgets/stores/ProductList';
import RewardBadge from '../../widgets/RewardBadge';
import ImageWithFallback from '../../widgets/ImageWithFallback';

import { ReactComponent as CoinIcon } from '../../assets/icons/coin.svg';
import ProductDeleteDialog from '../../widgets/stores/ProductDeleteDialog';
import StockDialog from '../../widgets/stores/StockDialog';

const StorePage = () => {
    const { user, hasPermission } = useContext(UserContext);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [selectedPlayer, setSelectedProduct] = useState(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [isStockDialogOpen, setIsStockDialogOpen] = useState(false);

    const [searchTerm, setSearchTerm] = useState('');
    const [gameId, setGameId] = useState('');
    const [games, setGames] = useState([]);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    useEffect(() => {
        fetchProducts();
    }, [user.company.id]);

    const fetchProducts = async () => {
        try {
            setLoading(true);

            const data = await RequestService.getProducts(
                user.company.id,
                searchTerm,
                gameId
            );
            setProducts(Array.isArray(data) ? data : []);
        } catch (err) {
            setError('Erro ao carregar produtos.');
            setProducts([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const fetchGames = async () => {
            try {
                const gameData = await RequestService.getGames(user.company.id);
                setGames(Array.isArray(gameData) ? gameData : []);
            } catch (err) {
                console.error('Erro ao carregar jogos:', err);
            }
        };

        fetchGames();
    }, [user.company.id]);

    const handleRowClick = (player) => {
        if (hasPermission('STORE_UPDATE')) {
            setSelectedProduct(player);
            setIsDialogOpen(true);
        }
    };

    const handleOpenStock = (stock) => {
        if (hasPermission('STORE_UPDATE')) {
            setSelectedProduct(stock);
            setIsStockDialogOpen(true);
        }
    };

    const handleCloseStock = () => {
        setIsStockDialogOpen(false);
        setSelectedProduct(null);
    };

    const handleCloseDialog = () => {
        setIsDialogOpen(false);
        setSelectedProduct(null);
    };

    const handleOpenAddDialog = () => setIsAddDialogOpen(true);
    const handleCloseAddDialog = () => setIsAddDialogOpen(false);
    const handleOpenDeleteDialog = (product) => {
        setSelectedProduct(product);
        setIsDeleteDialogOpen(true);
    };

    const handleCloseDeleteDialog = () => {
        setIsDeleteDialogOpen(false);
        setSelectedProduct(null);
    };

    const handleSuccessDelete = async () => {
        const data = await RequestService.getProducts(user.company.id);
        setProducts(data);
        setSnackbarMessage(
            selectedPlayer?.fulfillmentType === 'DIGITAL'
                ? 'Conteúdo removido da loja com sucesso!'
                : 'Recompensa excluída com sucesso!'
        );
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
    };

    const handlePublication = async (product) => {
        await RequestService.updateProductPublication(product.productId, true);
        await fetchProducts();
        setSnackbarMessage('Conteúdo publicado novamente!');
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
    };

    const handleSucessProduct = async (
        message = 'Operação realizada com sucesso!'
    ) => {
        const data = await RequestService.getProducts(user.company.id);
        setProducts(data);

        setSnackbarMessage(message);
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
    };

    const handleSnackbarClose = () => {
        setSnackbarOpen(false);
    };

    if (loading) return <div>Carregando...</div>;
    if (error) return <div>{error}</div>;

    const renderEmptyState = () => (
        <Box sx={{ textAlign: 'center', mt: 4, color: '#666' }}>
            <Typography variant="h6">Nenhuma recompensa disponível.</Typography>
            <Typography variant="body2">
                As recompensas criadas aparecerão aqui.
            </Typography>
        </Box>
    );

    const renderMobileState = () => {
        return products.length === 0 ? (
            renderEmptyState()
        ) : (
            <ProductList
                products={products}
                shouldEdit={hasPermission('STORE_UPDATE')}
                shouldDelete={hasPermission('STORE_DELETE')}
                onUserClick={handleRowClick}
                onDeleteClick={handleOpenDeleteDialog}
                onStockClick={handleOpenStock}
                onPublicationClick={handlePublication}
            />
        );
    };

    const renderDesktopState = () => {
        return products.length === 0 ? (
            renderEmptyState()
        ) : (
            <Grid container justifyContent="center" xs={12}>
                <Box sx={{ width: '100%', overflowX: 'auto', mt: 2 }}>
                    <TableComponent
                        columns={columns}
                        rows={products}
                        getRowId={(row) => row.productId}
                    />
                </Box>
            </Grid>
        );
    };

    const columns = [
        {
            label: 'Imagem',
            field: 'photo',
            align: 'center',
            renderCell: (value, row) => (
                <Box
                    sx={{
                        width: 172,
                        height: 112,
                        overflow: 'hidden',
                        borderRadius: '8px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        border: '1px solid #E0E0E0',
                        backgroundColor: '#FFFFFF',
                    }}
                >
                    <ImageWithFallback
                        src={row.photoUrl}
                        alt={row.name}
                        fallbackSrc="../../assets/images/gameup_dark.png"
                        styles={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                        }}
                    />
                </Box>
            ),
        },
        {
            label: 'Título, Descrição e Custo',
            field: 'details',
            align: 'left',
            renderCell: (value, row, stopPropagation) => (
                <Stack direction="column" spacing={1}>
                    <Typography variant="body2" sx={{ color: '#292929' }}>
                        {row.name}
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{ fontSize: '14px', color: '#292929' }}
                    >
                        {row.description}
                    </Typography>
                    <RewardBadge
                        icon={
                            <CoinIcon
                                style={{ color: '#134F99', fontSize: '24px' }}
                            />
                        }
                        value={row.price}
                    />
                    <Chip
                        label={row.fulfillmentType === 'DIGITAL' ? 'Conteúdo digital' : 'Produto físico'}
                        size="small"
                    />
                </Stack>
            ),
        },
        {
            label: 'Estoque',
            field: 'quantity',
            align: 'center',
            renderCell: (value, row) =>
                row.fulfillmentType === 'DIGITAL' ? 'Ilimitado' : row.quantity,
        },
        {
            label: 'Jogos',
            field: 'games',
            align: 'center',
            renderCell: (value, row) => (
                <Stack direction="column" spacing={1}>
                    {row.games.map((game, index) => (
                        <Chip
                            key={game.id}
                            label={game.name}
                            size="small"
                            color="primary"
                        />
                    ))}
                </Stack>
            ),
        },
        {
            label: 'Status',
            field: 'status',
            align: 'center',
            renderCell: (value, row) => (
                <Chip
                    label={row.fulfillmentType === 'DIGITAL'
                        ? (row.published ? 'Publicado' : 'Despublicado')
                        : (row.quantity > 0 ? 'Disponível' : 'Indisponível')}
                    size="small"
                    sx={{
                        bgcolor: (row.fulfillmentType === 'DIGITAL' ? row.published : row.quantity > 0)
                            ? '#97CC04' : '#CCCCCC',
                        color: '#000',
                    }}
                />
            ),
        },
        {
            label: '',
            field: 'actions',
            align: 'right',
            renderCell: (value, row, stopPropagation) => (
                <Stack direction="row" spacing={2}>
                    {hasPermission('STORE_UPDATE') && row.fulfillmentType !== 'DIGITAL' && (
                        <Link
                            component="button"
                            onClick={(event) => {
                                stopPropagation(event);
                                setSelectedProduct(row);
                                setIsStockDialogOpen(true);
                            }}
                            underline="none"
                            className="action-link"
                        >
                            <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                            >
                                <Inventory2OutlinedIcon
                                    fontSize="small"
                                    color="#1F83FF"
                                />
                                <Typography>Estoque</Typography>
                            </Stack>
                        </Link>
                    )}
                    {hasPermission('STORE_UPDATE') && (
                        <Link
                            component="button"
                            onClick={(event) => {
                                stopPropagation(event);
                                setSelectedProduct(row);
                                setIsDialogOpen(true);
                            }}
                            underline="none"
                            className="action-link"
                        >
                            <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                            >
                                <EditOutlinedIcon
                                    fontSize="small"
                                    color="#1F83FF"
                                />
                                <Typography>Editar</Typography>
                            </Stack>
                        </Link>
                    )}
                    {hasPermission('STORE_DELETE') && (
                        <Link
                            component="button"
                            onClick={() => row.fulfillmentType === 'DIGITAL' && !row.published
                                ? handlePublication(row)
                                : handleOpenDeleteDialog(row)}
                            underline="none"
                            className="action-link"
                            sx={{ color: 'red' }}
                        >
                            <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                                color="#1F83FF"
                            >
                                <DeleteOutlinedIcon
                                    fontSize="small"
                                    sx={{
                                        color: 'red',
                                    }}
                                />
                                <Typography color="red">
                                    {row.fulfillmentType === 'DIGITAL'
                                        ? (row.published ? 'Remover da loja' : 'Republicar')
                                        : 'Excluir'}
                                </Typography>
                            </Stack>
                        </Link>
                    )}
                </Stack>
            ),
        },
    ];

    return (
        <Box>
            <Grid container justifyContent="start">
                <Breadcrumbs aria-label="breadcrumb">
                    <Link underline="hover" color="inherit" href="/">
                        Início
                    </Link>
                    <Link underline="hover" color="inherit">
                        Loja
                    </Link>
                    <Typography sx={{ color: 'text.primary' }}>
                        Recompensas
                    </Typography>
                </Breadcrumbs>
            </Grid>

            <Grid container xs={12} sx={{ mt: 3 }}>
                <Typography
                    sx={{
                        fontFamily: 'Poppins',
                        fontSize: 24,
                        fontWeight: '500',
                        color: '#0C3466',
                        mb: 2,
                    }}
                >
                    Recompensas
                </Typography>
            </Grid>

            <Grid
                container
                spacing={2}
                alignItems="center"
                justifyContent="space-between"
                wrap="wrap"
            >
                <Grid container item xs={12} md={9} spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            label="Pesquisar Nome"
                            variant="outlined"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    fetchProducts();
                                }
                            }}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <FormControl
                            sx={{ minWidth: 200, width: '100%' }}
                            fullWidth
                        >
                            <InputLabel>Filtrar por Jogo</InputLabel>
                            <Select
                                value={gameId}
                                onChange={(e) => {
                                    setGameId(e.target.value);
                                    fetchProducts();
                                }}
                            >
                                <MenuItem value="">Todos</MenuItem>
                                {games.map((game) => (
                                    <MenuItem key={game.id} value={game.id}>
                                        {game.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6} md={3}>
                        <Button
                            variant="contained"
                            sx={{ minHeight: '47px' }}
                            onClick={fetchProducts}
                            fullWidth
                        >
                            Aplicar Filtros
                        </Button>
                    </Grid>
                </Grid>

                <Grid item xs={12} md="auto">
                    {hasPermission('STORE_CREATE') && (
                        <Button
                            variant="contained"
                            sx={{ minHeight: '47px' }}
                            endIcon={<AddCircleOutlineOutlinedIcon />}
                            onClick={handleOpenAddDialog}
                        >
                            Nova Recompensa
                        </Button>
                    )}
                </Grid>
            </Grid>

            {isMobile ? renderMobileState() : renderDesktopState()}

            <ProductDialog
                open={isDialogOpen}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        handleCloseDialog();
                    }
                }}
                onSuccess={handleSucessProduct}
                product={selectedPlayer}
                user={user}
            />

            <NewProductDialog
                open={isAddDialogOpen}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        handleCloseAddDialog();
                    }
                }}
                onSuccess={handleSucessProduct}
                user={user}
            />

            <ProductDeleteDialog
                open={isDeleteDialogOpen}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        handleCloseDeleteDialog();
                    }
                }}
                product={selectedPlayer}
                onSuccess={handleSuccessDelete}
            />

            <StockDialog
                open={isStockDialogOpen}
                onClose={() => {
                    setIsStockDialogOpen(false);
                    setSelectedProduct(null);
                }}
                product={{
                    id: selectedPlayer?.productId,
                    name: selectedPlayer?.name,
                    description: selectedPlayer?.description,
                    imageUrl: selectedPlayer?.photoUrl,
                    price: selectedPlayer?.price,
                    gameTag: selectedPlayer?.games?.[0]?.name,
                    statusLabel: selectedPlayer
                        ? selectedPlayer.quantity > 0
                            ? 'Disponível'
                            : 'Indisponível'
                        : undefined,
                    quantity: selectedPlayer?.quantity,
                    storeId: selectedPlayer?.storeId,
                }}
                currentStock={selectedPlayer?.quantity ?? 0}
                storeId={user?.company?.storeId}
                onConfirm={async (dto) => {
                    try {
                        await RequestService.adjustStock(dto);
                        await fetchProducts();
                        setSnackbarMessage('Estoque atualizado com sucesso!');
                        setSnackbarSeverity('success');
                        setSnackbarOpen(true);
                    } catch (e) {
                        console.error(e);
                        setSnackbarMessage('Falha ao ajustar estoque.');
                        setSnackbarSeverity('error');
                        setSnackbarOpen(true);
                    } finally {
                        setIsStockDialogOpen(false);
                        setSelectedProduct(null);
                    }
                }}
            />

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={handleSnackbarClose}
                    severity={snackbarSeverity}
                    variant="filled"
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default StorePage;
